import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

function EmailTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'preview'
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const functions = getFunctions();

  // Load templates on mount - Direct Firestore access
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      // Read directly from Firestore
      const templatesRef = collection(db, 'email_templates');
      const q = query(templatesRef, orderBy('type'), orderBy('name'));
      
      onSnapshot(q, (snapshot) => {
        const templatesList = [];
        snapshot.forEach((doc) => {
          templatesList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setTemplates(templatesList);
        setLoading(false);
      }, (error) => {
        console.error('Failed to load templates:', error);
        showMessage('error', 'Failed to load templates');
        setLoading(false);
      });
    } catch (error) {
      console.error('Failed to load templates:', error);
      showMessage('error', 'Failed to load templates');
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setHtmlContent(template.htmlContent || '');
    setSubject(template.subject || '');
  };

  const updateTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      
      // Only call cloud function to sync with SendGrid
      // Firestore updates happen automatically via the function
      const updateFunc = httpsCallable(functions, 'apiUpdateTemplateContent');
      const result = await updateFunc({
        templateId: selectedTemplate.id,
        subject: subject,
        htmlContent: htmlContent,
        isActive: true
      });

      if (result.data.success) {
        showMessage('success', '✅ Template updated successfully in SendGrid!');
        // Template list will auto-update via onSnapshot
      } else {
        showMessage('error', '❌ Error: ' + result.data.error);
      }
    } catch (error) {
      console.error('Update failed:', error);
      showMessage('error', 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }

    try {
      setSendingTest(true);
      const sendTestFunc = httpsCallable(functions, 'apiSendTestEmail');
      const result = await sendTestFunc({
        templateId: selectedTemplate.id,
        testEmail: testEmail,
        personalizedData: {
          userName: 'Test User',
          dashboardUrl: 'https://fluxara.ai',
          creditsAmount: 100,
          projectsGenerated: 5
        }
      });

      if (result.data.success) {
        showMessage('success', '✅ Test email sent to ' + testEmail);
        setShowTestDialog(false);
        setTestEmail('');
      } else {
        showMessage('error', '❌ Failed to send test email');
      }
    } catch (error) {
      console.error('Test email failed:', error);
      showMessage('error', 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'auth': return '🔐';
      case 'payment': return '💳';
      case 'usage': return '⚡';
      case 'lifecycle': return '📧';
      default: return '📨';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📧 Email Template Manager</h1>
          <p className="text-gray-600">Manage and customize your email templates</p>
        </div>

        {/* Message Banner */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-turquoise-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading templates...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Template List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-4">
                <h2 className="text-lg font-semibold mb-4">Templates ({templates.length})</h2>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {templates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedTemplate?.id === template.id
                          ? 'bg-turquoise-50 border-2 border-turquoise-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{getTypeIcon(template.type)}</span>
                            <span className="font-medium text-sm">{template.name}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            {template.isTransactional ? (
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">Transactional</span>
                            ) : (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Marketing</span>
                            )}
                            <span className={`px-2 py-0.5 rounded ${
                              template.isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {template.isActive ? 'Active' : 'Draft'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Editor */}
            <div className="lg:col-span-2">
              {selectedTemplate ? (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  {/* Template Info */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-turquoise-50 to-cyan-50 rounded-lg border border-turquoise-200">
                    <h2 className="text-xl font-bold text-gray-900 mb-3">{selectedTemplate.name}</h2>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Type:</span>
                        <span className="ml-2 font-medium">
                          {selectedTemplate.isTransactional ? '🔐 Transactional' : '💌 Marketing'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Category:</span>
                        <span className="ml-2 font-medium">{selectedTemplate.category}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">SendGrid ID:</span>
                        <span className="ml-2 font-mono text-xs">{selectedTemplate.sendgridTemplateId || 'Not synced'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <span className={`ml-2 font-medium ${
                          selectedTemplate.isActive ? 'text-green-600' : 'text-gray-600'
                        }`}>
                          {selectedTemplate.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <span className="text-gray-600 text-sm">Variables:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedTemplate.variables?.map(v => (
                          <span key={v} className="bg-white px-2 py-1 rounded text-xs font-mono border border-turquoise-300">
                            {`{{${v}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Subject Editor */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-turquoise-500 focus:border-transparent"
                      placeholder="Email subject line"
                    />
                  </div>

                  {/* View Mode Tabs */}
                  <div className="mb-4 flex gap-2 border-b border-gray-200">
                    <button
                      onClick={() => setViewMode('edit')}
                      className={`px-4 py-2 font-medium text-sm transition-colors ${
                        viewMode === 'edit'
                          ? 'text-turquoise-600 border-b-2 border-turquoise-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      📝 Edit HTML
                    </button>
                    <button
                      onClick={() => setViewMode('preview')}
                      className={`px-4 py-2 font-medium text-sm transition-colors ${
                        viewMode === 'preview'
                          ? 'text-turquoise-600 border-b-2 border-turquoise-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      👁️ Preview
                    </button>
                  </div>

                  {/* HTML Editor / Preview */}
                  <div className="mb-6">
                    {viewMode === 'edit' ? (
                      <>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          HTML Content
                        </label>
                        <textarea
                          value={htmlContent}
                          onChange={(e) => setHtmlContent(e.target.value)}
                          className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-turquoise-500 focus:border-transparent"
                          placeholder="HTML email content"
                        />
                      </>
                    ) : (
                      <>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Preview
                        </label>
                        <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
                          <div className="bg-gray-100 border-b border-gray-300 p-3">
                            <div className="text-sm">
                              <span className="font-semibold text-gray-700">Subject:</span>
                              <span className="ml-2 text-gray-900">{subject || '(No subject)'}</span>
                            </div>
                          </div>
                          <div className="p-4 max-h-96 overflow-y-auto bg-white">
                            {htmlContent ? (
                              <iframe
                                srcDoc={htmlContent}
                                title="Email Preview"
                                className="w-full min-h-[500px] border-0"
                                sandbox="allow-same-origin"
                              />
                            ) : (
                              <div className="text-center py-12 text-gray-500">
                                <p>No HTML content to preview</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={updateTemplate}
                      disabled={saving}
                      className="flex-1 bg-turquoise-500 hover:bg-turquoise-600 disabled:bg-gray-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Updating...
                        </>
                      ) : (
                        <>
                          💾 Update Template
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => setShowTestDialog(true)}
                      className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                    >
                      📧 Send Test
                    </button>
                  </div>

                  {selectedTemplate.isTransactional && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ <strong>Transactional Email:</strong> This email will always be sent, regardless of user preferences. Do not include promotional content.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                  <div className="text-6xl mb-4">📧</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Template</h3>
                  <p className="text-gray-600">Choose a template from the list to edit</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Test Email Dialog */}
      {showTestDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📧 Send Test Email</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Send a test email for <span className="font-semibold">{selectedTemplate?.name}</span> to verify how it looks.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Recipient Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendTest()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-turquoise-500 focus:border-transparent"
                placeholder="example@email.com"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTestDialog(false);
                  setTestEmail('');
                }}
                disabled={sendingTest}
                className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={sendTest}
                disabled={sendingTest || !testEmail}
                className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {sendingTest ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    📧 Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplateEditor;
